import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ShieldAlert, ShieldX, Loader2, Car, User, Calendar, Gauge, Wrench } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function Row({ icon: Icon, label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-[var(--kk-border)] last:border-0">
      <div className="w-8 h-8 rounded-full bg-[var(--kk-bg-sub)] flex items-center justify-center shrink-0">
        <Icon size={14} />
      </div>
      <div className="flex-1">
        <div className="kk-overline text-[0.55rem]">{label}</div>
        <div className="font-semibold text-sm break-words">{value}</div>
      </div>
    </div>
  );
}

export default function VerifyWarranty() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/warranties/verify/${token}`);
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (err) {
        const detail = err?.response?.data?.detail || "Unable to verify certificate";
        if (!cancelled) setState({ loading: false, data: null, error: detail });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const statusBadge = (() => {
    if (!state.data) return null;
    const s = state.data.status;
    if (s === "active") return { cls: "bg-[var(--kk-lime)] text-[var(--kk-black)]", label: "Active", Icon: ShieldCheck };
    if (s === "claimed") return { cls: "bg-amber-200 text-amber-900", label: "Claimed", Icon: ShieldAlert };
    if (s === "expired") return { cls: "bg-red-200 text-red-900", label: "Expired", Icon: ShieldX };
    if (s === "void") return { cls: "bg-neutral-300 text-neutral-900", label: "Voided", Icon: ShieldX };
    return { cls: "bg-neutral-200 text-neutral-900", label: s, Icon: ShieldCheck };
  })();

  return (
    <div className="min-h-screen bg-[var(--kk-bg)] text-[var(--kk-black)]" data-testid="verify-warranty-page">
      <div className="max-w-xl mx-auto px-5 py-8 md:py-14">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-7 h-7 rounded-sm bg-[var(--kk-black)] text-[var(--kk-lime)] flex items-center justify-center font-display text-xs">KK</div>
          <div>
            <div className="font-display text-lg tracking-wider uppercase leading-none">Karr Kraft</div>
            <div className="kk-overline text-[0.55rem]">Warranty Verification Portal</div>
          </div>
        </div>

        {state.loading && (
          <div className="bg-[var(--kk-surface)] rounded-xl p-8 flex flex-col items-center text-center">
            <Loader2 className="animate-spin" />
            <div className="mt-3 text-sm text-[var(--kk-muted)]">Verifying certificate…</div>
          </div>
        )}

        {state.error && (
          <div className="bg-[var(--kk-surface)] rounded-xl p-8 text-center" data-testid="verify-error">
            <ShieldX size={42} className="mx-auto text-red-500" />
            <h1 className="font-display text-2xl uppercase mt-4">Certificate Invalid</h1>
            <p className="text-sm text-[var(--kk-muted)] mt-2">{state.error}</p>
            <p className="text-xs text-[var(--kk-muted)] mt-6">
              If you scanned this from a physical certificate, please contact Karr Kraft support for verification.
            </p>
          </div>
        )}

        {state.data && statusBadge && (
          <div className="bg-[var(--kk-surface)] rounded-xl overflow-hidden shadow-sm">
            <div className={`px-6 py-5 flex items-center justify-between ${statusBadge.cls}`}>
              <div>
                <div className="kk-overline text-[0.55rem] opacity-80">Certificate status</div>
                <div className="font-display text-2xl uppercase">{statusBadge.label}</div>
              </div>
              <statusBadge.Icon size={34} strokeWidth={1.5} />
            </div>

            <div className="px-6 pt-4 pb-2">
              <div className="kk-overline">Certificate</div>
              <div className="font-mono text-sm" data-testid="verify-code">{state.data.code}</div>
              <div className="text-xs text-[var(--kk-muted)] mt-0.5">Issued {(state.data.issued_at || "").slice(0, 10)}</div>
            </div>

            <div className="px-6 pb-6 pt-3">
              <Row icon={ShieldCheck} label="Coverage type" value={(state.data.type || "").replace(/_/g, " ").toUpperCase()} />
              <Row icon={Calendar} label="Valid from" value={state.data.start_date} />
              <Row icon={Calendar} label="Valid until" value={state.data.end_date} />
              {state.data.kms_limit && (
                <Row icon={Gauge} label="Kilometre limit" value={`${state.data.start_kms ?? 0} → ${state.data.kms_limit} km`} />
              )}
              {state.data.car && (
                <Row
                  icon={Car}
                  label="Vehicle"
                  value={`${state.data.car.make || ""} ${state.data.car.model || ""} ${state.data.car.year || ""} ${state.data.car.registration_no ? `• ${state.data.car.registration_no}` : ""}`.trim()}
                />
              )}
              {state.data.customer && <Row icon={User} label="Registered owner" value={state.data.customer.name} />}
              {state.data.covered_items?.length > 0 && (
                <div className="pt-3">
                  <div className="kk-overline text-[0.55rem]">Covered items</div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {state.data.covered_items.map((it, idx) => (
                      <span key={idx} className="kk-pill kk-pill-muted" data-testid={`covered-item-${idx}`}>
                        <Wrench size={10} /> {it.name || it.sku}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {state.data.claim_count > 0 && (
                <div className="mt-4 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  {state.data.claim_count} claim{state.data.claim_count !== 1 ? "s" : ""} logged on this warranty.
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-8 text-[0.65rem] text-center text-[var(--kk-muted)] uppercase tracking-wider">
          Karr Kraft Automotive Pvt. Ltd. — Signed certificate
        </div>
      </div>
    </div>
  );
}
